// @flow
import * as React from 'react';
import {useEffect, useState} from 'react';
import {Body} from 'sail/Text';
import {ContentGroupItem} from 'docs/util';
import * as Sentry from '@sentry/react';
import Spinner from 'sail/Spinner';
import Box from 'sail/Box';
import {Table, TabGroup, TableHeaderCell, TableCell} from 'docs/components';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useAccount, useApi} from 'docs/data';

const messages = defineMessages({
  tableHeader: {
    id: 'docsShell.currencies.alternativeCurrencySettlementFees.TableHeader',
    description:
      'Introduces a table of settlement fees for alternative currencies',
    defaultMessage:
      'Acquiring fees for card usage in alternative currencies for accounts created in {countryName}.',
  },
  dropDownLabel: {
    id: 'docsShell.currencies.alternativeCurrencySettlementFees.DropDownLabel',
    description: 'Label for country drop down selector',
    defaultMessage: 'Viewing supported settlement currencies for',
  },
  loading: {
    id: 'docsShell.currencies.alternativeCurrencySettlementFees.Loading',
    description: 'A loading message displayed to a user while a table loads',
    defaultMessage: 'Loading a table of settlement fees',
  },
  error: {
    id: 'docsShell.currencies.alternativeCurrencySettlementFees.Error',
    description:
      'An error message displayed to a user while a table fails to load',
    defaultMessage: 'Unable to load the table of settlement fees',
  },
  currencyHeader: {
    id: 'docsShell.currencies.alternativeCurrencySettlementFees.CurrencyHeader',
    description: 'Currency header in the table of settlement fees ',
    defaultMessage: 'Currency',
  },
  chargeFeeHeader: {
    id:
      'docsShell.currencies.alternativeCurrencySettlementFees.ChargeFeeHeader',
    description: 'Charge fee header in the table of settlement fees ',
    defaultMessage: 'Charge fee',
  },
  disputeFeeHeader: {
    id:
      'docsShell.currencies.alternativeCurrencySettlementFees.DisputeFeeHeader',
    description: 'Dispute fee header in the table of settlement fees ',
    defaultMessage: 'Dispute fee',
  },
});

type DropdownProps = {|
  items: Array<ContentGroupItem>,
  defaultValue?: string,
|};

const Dropdown = ({items, defaultValue}: DropdownProps) => {
  const intl = useIntl();
  const dropDownLabel = intl.formatMessage(messages.dropDownLabel);

  return (
    <TabGroup
      selector="drop-down"
      items={items}
      label={dropDownLabel}
      defaultValue={defaultValue}
    />
  );
};

// Used on /docs/currencies/settlement-fees
export const AlternativeCurrencySettlementFees = () => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState(false);
  const {account} = useAccount();
  const {api} = useApi();
  const accountCountry = account.country.code;

  useEffect(() => {
    api
      .getAlternativeCurrencySettlementFees()
      .then((response) => {
        setCurrencies(response.countries);
      })
      .catch((err) => {
        setError(true);
        if (Sentry && Sentry.captureMessage) {
          Sentry.captureMessage(
            `Failed to load settlement fees for alternative currencies`,
          );
        }
      });
  }, [api]);

  if (error) {
    return (
      <Box flex={{justifyContent: 'center'}} padding={32}>
        <Body>
          <FormattedMessage {...messages.error} />
        </Body>
      </Box>
    );
  }

  if (!currencies.length) {
    return (
      <Box flex={{justifyContent: 'center'}} padding={32}>
        <Spinner
          text={<FormattedMessage {...messages.loading} />}
          size="large"
        />
      </Box>
    );
  }

  const items = currencies.map((c) => {
    if (!c.currencies.length) {
      return null;
    }

    return new ContentGroupItem(
      c.code,
      `${c.name || ''} (${c.code})`,
      (
        <AlternativeCurrencySettlementFeesTable
          countryCode={c.code}
          countryName={c.readable_name}
          rows={c.currencies}
        />
      ),
    );
  });

  return (
    <Dropdown items={items.filter(Boolean)} defaultValue={accountCountry} />
  );
};

type AlternativeCurrencySettlementFeesProps = {|
  countryCode: string,
  countryName: string,
  rows: Array<any>,
|};

// Used on /docs/payouts
const AlternativeCurrencySettlementFeesTable = ({
  countryCode,
  countryName,
  rows,
}: AlternativeCurrencySettlementFeesProps) => {
  if (!rows) {
    return null;
  }

  return (
    <>
      <Body>
        <FormattedMessage {...messages.tableHeader} values={{countryName}} />
      </Body>
      <Table type="striped">
        <thead>
          <tr>
            <TableHeaderCell>
              <FormattedMessage {...messages.currencyHeader} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.chargeFeeHeader} />
            </TableHeaderCell>
            <TableHeaderCell>
              <FormattedMessage {...messages.disputeFeeHeader} />
            </TableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${countryCode}-${r.currency}`}>
              <TableCell>{r.currency.toUpperCase()}</TableCell>
              <TableCell>
                {r.charge_fees.map((fee, index) => (
                  <p key={`${countryCode}-${r.currency}-charge${index}`}>
                    {fee.price} {fee.description}
                  </p>
                ))}
              </TableCell>
              <TableCell>
                {r.dispute_fees.map((fee, index) => (
                  <p key={`${countryCode}-${r.currency}-dispute${index}`}>
                    {fee.price}
                  </p>
                ))}
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};
